import React from 'react';
import Tables from '../../components/organisms/Tables/Tables';
import CurrencyAmount from '../../components/organisms/Tables/CellRenderer/CurrencyAmount';
import { getInvestmentStatusTag } from '../utils/investment';
import dayjs from 'dayjs';

export type ClientPayoutProductTab = 'INVOICE_DISCOUNTING' | 'ASSET_LEASING';

const baseColumns = [
  { id: 'clientName', label: 'Client', minWidth: 160, align: 'left' },
  { id: 'clientPhone', label: 'Phone', minWidth: 120, align: 'left' },
  { id: 'dealName', label: 'Deal Name', minWidth: 160, align: 'left' },
  { id: 'investedAmount', label: 'Invested Amount', minWidth: 120, align: 'left' },
  { id: 'expectedPayoutAmount', label: 'Expected Payout Amount', minWidth: 140, align: 'left' },
  { id: 'expectedPayoutDate', label: 'Expected Payout Date', minWidth: 130, align: 'left' },
  { id: 'investedAt', label: 'Invested On', minWidth: 120, align: 'left' },
  { id: 'investmentState', label: 'Investment Status', minWidth: 120, align: 'left' },
];

const assetLeasingColumns = [
  { id: 'clientName', label: 'Client', minWidth: 160, align: 'left' },
  { id: 'clientPhone', label: 'Phone', minWidth: 120, align: 'left' },
  { id: 'dealName', label: 'Deal Name', minWidth: 160, align: 'left' },
  { id: 'investedAmount', label: 'Invested Amount', minWidth: 120, align: 'left' },
  { id: 'expectedPayoutAmount', label: 'Expected Payout Amount', minWidth: 140, align: 'left' },
  { id: 'maturityDate', label: 'Maturity Date', minWidth: 130, align: 'left' },
  { id: 'investedAt', label: 'Invested On', minWidth: 120, align: 'left' },
  { id: 'investmentState', label: 'Investment Status', minWidth: 120, align: 'left' },
];

const SORTABLE_COLUMNS = {
  expectedPayoutDate: 'payoutDate',
  maturityDate: 'payoutDate',
  investedAt: 'time',
  investedAmount: 'amount',
  expectedPayoutAmount: 'payoutAmount',
  dealName: 'dealName',
};

export function formatDealName(dealName) {
  return dealName || '-';
}

export function mapClientPayoutRows(items = [], productTab: ClientPayoutProductTab = 'INVOICE_DISCOUNTING') {
  return items.map((item) => {
    const expectedPayoutDate = item.expectedPayoutDate
      ? dayjs(item.expectedPayoutDate).format('DD MMM YYYY')
      : '-';

    const row: Record<string, any> = {
      clientName: item.clientName || '-',
      clientPhone: item.clientPhone || '-',
      dealName: formatDealName(item.dealName),
      investedAmount: <CurrencyAmount amount={item.investedAmount} />,
      expectedPayoutAmount: <CurrencyAmount amount={item.expectedPayoutAmount} />,
      investedAt: item.investedAt ? dayjs(item.investedAt).format('DD MMM YYYY') : '-',
      investmentState: getInvestmentStatusTag(item.investmentState) || item.investmentState || '-',
    };

    if (productTab === 'ASSET_LEASING') {
      // Maturity date for AL comes from scheduled payout_date on client_investment
      row.maturityDate = expectedPayoutDate;
    } else {
      row.expectedPayoutDate = expectedPayoutDate;
    }

    return row;
  });
}

interface ClientPayoutsTableProps {
  data?: {
    items?: any[];
    totalItems?: number;
    currentPage?: number;
    pageSize?: number;
  };
  productTab: ClientPayoutProductTab;
  handleChangePage: (event: unknown, newPage: number) => void;
  handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleChangeSort: (event: unknown, columnId: string) => void;
  orderBy: string;
  order: 'asc' | 'desc';
  pageSize: number;
  pageNo: number;
  loading?: boolean;
}

const ClientPayoutsTable: React.FC<ClientPayoutsTableProps> = ({
  data,
  productTab,
  handleChangePage,
  handleChangeRowsPerPage,
  handleChangeSort,
  orderBy,
  order,
  pageSize,
  pageNo,
  loading = false,
}) => {
  const columns = productTab === 'ASSET_LEASING' ? assetLeasingColumns : baseColumns;
  const rows = mapClientPayoutRows(data?.items || [], productTab);

  return (
    <Tables
      columns={columns}
      rows={rows}
      handleChangePage={handleChangePage}
      handleChangeRowsPerPage={handleChangeRowsPerPage}
      handleChangeSort={handleChangeSort}
      handleRowClick={() => {}}
      sortableColumns={SORTABLE_COLUMNS}
      orderBy={orderBy}
      order={order}
      rowsPerPage={pageSize}
      page={pageNo}
      totalData={data?.totalItems || 0}
      loading={loading}
      disableCursor
    />
  );
};

export default ClientPayoutsTable;
